// Skills — o registro de skills do Claude Code que o LiteLLM mantém e serve em
// `/claude-code/marketplace.json`. Contrato: docs/screens/13-skills.md.
//
// Correção sobre o contrato original: ele apontava para `GET /v1/skills`, que é
// o passthrough para a Skills API da Anthropic e exige ANTHROPIC_API_KEY. As
// skills do projeto não vivem lá — vivem em `GET /claude-code/plugins`, que é o
// gateway falando de si mesmo. Mesmo dono (LiteLLM), outro endpoint.
//
// Registrar uma skill não é colar um SKILL.md: o corpo pede `{name, source}`,
// onde source é a origem git (url + path + ref). É assim que as 414 entraram.

import { gateway, litellm, SourceError, type Skill } from "../api";
import { empty, errorBox, esc, head, pill, since, skeleton, table, toast } from "../ui";

const CSS = `
.skills-menu { position: relative; display: inline-block; }
.skills-menu summary { list-style: none; cursor: pointer; padding: 2px 8px; border-radius: 6px; }
.skills-menu summary::-webkit-details-marker { display: none; }
.skills-menu[open] summary { background: rgba(127,127,127,.2); }
.skills-menu-pop { position: absolute; right: 0; z-index: 10; padding: 6px; display: grid; gap: 4px; min-width: 130px; }
.skills-head-left { display: flex; align-items: center; gap: 12px; }
.skills-back { text-decoration: none; font-size: 18px; }
.skills-desc { color: var(--ink-3); max-width: 46ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.skills-busca { width: 240px; }
.skills-conta { color: var(--ink-3); font-size: 12px; margin-bottom: 10px; }
.skills-src { font-size: 11.5px; color: var(--ink-3); }
`;
if (!document.getElementById("css-skills")) {
  const st = document.createElement("style");
  st.id = "css-skills";
  st.textContent = CSS;
  document.head.append(st);
}

/** "Aug 9, 2026", como no app de referência. */
const dataCurta = (iso?: string): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "–";

async function load(el: HTMLElement, header: string, body: () => Promise<string>) {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

export const skills = async (el: HTMLElement): Promise<void> => {
  const nome = decodeURIComponent(location.hash.split("/")[2] ?? "");
  if (nome === "new") return criar(el);
  if (nome) return detalhe(el, nome);
  return lista(el);
};

// -------------------------------------------------------------------- lista

/** Filtro só no cliente: as 414 já vieram numa resposta, e uma busca por
 *  substring não justifica ida ao servidor. */
let busca = "";

async function lista(el: HTMLElement): Promise<void> {
  const header = head({
    title: "Skills",
    actions: `<input class="input skills-busca" id="skills-q" placeholder="Filtrar…" value="${esc(busca)}" />
      <a class="btn btn-primary" href="#/skills/new">+ Create Skill</a>`,
  });

  await load(el, header, async () => {
    const todas = await litellm.skills();
    const q = busca.trim().toLowerCase();
    const vis = q
      ? todas.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            (s.description ?? "").toLowerCase().includes(q) ||
            (s.category ?? "").toLowerCase().includes(q),
        )
      : todas;

    if (!todas.length) {
      return empty(
        "Nenhuma skill registrada",
        "Uma skill é uma pasta com SKILL.md que a frota carrega sob demanda. Registre a primeira apontando a origem git dela.",
        `<a class="btn btn-primary" href="#/skills/new">+ Create Skill</a>`,
      );
    }

    const linhas = vis.map((s) => {
      const href = `#/skills/${encodeURIComponent(s.name)}`;
      // "Source" do spec = de onde a skill veio. Aqui é a categoria que o
      // registro carrega (BYMAV nas nossas), não uma origem inventada.
      const origem = s.category ?? s.namespace ?? "—";
      return `<tr>
        <td><a href="${href}">${esc(s.name)}</a>${
          s.version ? ` <span class="skills-src">v${esc(s.version)}</span>` : ""
        }</td>
        <td class="skills-desc" title="${esc(s.description ?? "")}">${esc(s.description ?? "")}</td>
        <td>${pill(origem, "idle")}</td>
        <td>${s.enabled ? pill("Enabled", "ok") : pill("Disabled", "idle")}</td>
        <td class="mono">${esc(dataCurta(s.updated_at ?? s.created_at))}</td>
        <td><details class="skills-menu"><summary aria-label="Ações de ${esc(s.name)}">⋮</summary>
          <div class="skills-menu-pop card">
            <button class="btn skills-tog" data-nome="${esc(s.name)}" data-ligar="${!s.enabled}">${
              s.enabled ? "Disable" : "Enable"
            }</button>
            <button class="btn skills-del" data-nome="${esc(s.name)}">Delete</button>
          </div>
        </details></td>
      </tr>`;
    });

    const conta = `<div class="skills-conta">${vis.length} de ${todas.length} skills${
      q ? ` para "${esc(busca)}"` : ""
    }</div>`;

    if (!vis.length) return conta + empty("Nada casou com o filtro", "Tente outro termo.");
    return conta + table(["Name", "Description", "Source", "Status", "Updated", ""], linhas);
  });

  const q = el.querySelector<HTMLInputElement>("#skills-q");
  if (q) {
    q.oninput = () => {
      busca = q.value;
      // Refiltra sem refetch: o input é recriado no re-render, então devolvemos
      // o cursor para o fim, senão digitar fica impossível.
      void lista(el).then(() => {
        const novo = el.querySelector<HTMLInputElement>("#skills-q");
        novo?.focus();
        novo?.setSelectionRange(novo.value.length, novo.value.length);
      });
    };
  }

  for (const b of el.querySelectorAll<HTMLButtonElement>(".skills-tog")) {
    b.onclick = async () => {
      b.disabled = true;
      try {
        await litellm.skillToggle(b.dataset.nome!, b.dataset.ligar === "true");
        toast(`${b.dataset.nome} ${b.dataset.ligar === "true" ? "habilitada" : "desabilitada"}`);
        await lista(el);
      } catch (e) {
        toast((e as SourceError).message);
        b.disabled = false;
      }
    };
  }

  for (const b of el.querySelectorAll<HTMLButtonElement>(".skills-del")) {
    b.onclick = async () => {
      const nome = b.dataset.nome!;
      if (!confirm(`Delete ${nome}?`)) return;
      b.disabled = true;
      try {
        await litellm.skillDelete(nome);
        toast(`${nome} removida`);
        await lista(el);
      } catch (e) {
        toast((e as SourceError).message);
        b.disabled = false;
      }
    };
  }
}

// ------------------------------------------------------------------ detalhe

async function detalhe(el: HTMLElement, nome: string): Promise<void> {
  const header = `
    <header class="page-head">
      <div class="skills-head-left">
        <a class="skills-back" href="#/skills" aria-label="Voltar">←</a>
        <h1 class="page-title">${esc(nome)}</h1>
      </div>
      <div class="page-actions"><a class="btn" href="#/skills">Cancel</a></div>
    </header>`;

  await load(el, header, async () => {
    const s = await gateway<Skill>(`litellm/claude-code/plugins/${encodeURIComponent(nome)}`);
    const src = s.source ?? {};
    const campo = (rot: string, val: string) => `
      <div class="field">
        <label class="label">${esc(rot)}</label>
        <input class="input" value="${esc(val)}" readonly />
      </div>`;

    // Somente leitura: o registro guarda a origem git, e o conteúdo do
    // SKILL.md vive no repositório apontado — não neste banco.
    return `
      <div class="card"><div class="card-body">
        ${campo("Name", s.name)}
        ${campo("Version", s.version ?? "—")}
        ${campo("Description", s.description ?? "—")}
        ${campo("Category", s.category ?? "—")}
        ${campo("Namespace", s.namespace ?? "—")}
        ${campo("Status", s.enabled ? "Enabled" : "Disabled")}
        ${campo("Source", `${src.source ?? "—"} · ${src.url ?? ""}`)}
        ${campo("Path", src.path ?? "—")}
        ${campo("Ref", src.ref ?? "—")}
        ${campo("Registered", `${dataCurta(s.created_at)} (${since(s.created_at ?? null)})`)}
        <span class="hint">O corpo do SKILL.md vive no repositório de origem, não neste registro — editar aqui seria inventar uma segunda verdade.</span>
      </div></div>`;
  });
}

// -------------------------------------------------------------------- criar

/** Registrar = apontar a origem git. É `{name, source}` obrigatórios no
 *  RegisterPluginRequest do openapi; o resto é metadado opcional. */
async function criar(el: HTMLElement): Promise<void> {
  const header = head({
    title: "Create Skill",
    actions: `<a class="btn" href="#/skills">Cancel</a>
      <button class="btn btn-primary" id="skills-save" type="submit" form="skills-form">Register</button>`,
  });

  el.innerHTML =
    header +
    `<form class="card" id="skills-form">
      <div class="field">
        <label class="label" for="sk-name">Name</label>
        <input class="input" id="sk-name" name="name" placeholder="plan-mode" autocomplete="off" required />
        <span class="hint">É o slug de invocação: /plan-mode.</span>
      </div>
      <div class="field">
        <label class="label" for="sk-url">Source repository</label>
        <input class="input" id="sk-url" name="url" placeholder="ssh://me@host/home/me/litellm" required />
      </div>
      <div class="field">
        <label class="label" for="sk-path">Path</label>
        <input class="input" id="sk-path" name="path" placeholder="skills/plan-mode" required />
        <span class="hint">Pasta que contém o SKILL.md dentro do repositório.</span>
      </div>
      <div class="field">
        <label class="label" for="sk-ref">Ref</label>
        <input class="input" id="sk-ref" name="ref" placeholder="main" value="main" />
      </div>
      <div class="field">
        <label class="label" for="sk-desc">Description</label>
        <textarea class="textarea" id="sk-desc" name="description" rows="3"></textarea>
      </div>
      <div class="field">
        <label class="label" for="sk-cat">Category</label>
        <input class="input" id="sk-cat" name="category" value="BYMAV" />
      </div>
      <div class="field">
        <label class="label" for="sk-ver">Version</label>
        <input class="input" id="sk-ver" name="version" value="1.0.0" />
      </div>
    </form>`;

  const form = el.querySelector<HTMLFormElement>("#skills-form")!;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    const d = new FormData(form);
    const nome = String(d.get("name") ?? "").trim();
    const url = String(d.get("url") ?? "").trim();
    const path = String(d.get("path") ?? "").trim();
    if (!nome || !url || !path) return toast("Name, repositório e path são obrigatórios");

    const botao = el.querySelector<HTMLButtonElement>("#skills-save")!;
    botao.disabled = true;
    try {
      await gateway("litellm/claude-code/plugins", {
        method: "POST",
        body: JSON.stringify({
          name: nome,
          source: { source: "git-subdir", url, path, ref: String(d.get("ref") ?? "main").trim() || "main" },
          version: String(d.get("version") ?? "").trim() || undefined,
          description: String(d.get("description") ?? "").trim() || undefined,
          category: String(d.get("category") ?? "").trim() || undefined,
        }),
      });
      toast(`${nome} registrada`);
      location.hash = "/skills";
    } catch (e) {
      toast((e as SourceError).message);
      botao.disabled = false;
    }
  });
}
